import { useDeferredValue, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { format } from "date-fns";
import { trpc } from "@/hooks/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

export const Route = createFileRoute("/brewcontrol/$nodeId/logs")({
  component: NodeLogs,
});

type LevelFilter = "notice" | "all";

const PAGE = 200;
const MAX_ROWS = 2000;
/** The node pushes about once a second; this is fresh enough to read along. */
const POLL_MS = 5_000;

/**
 * The node's diagnostic log as replicated up the link, newest first. What is
 * here is what the node kept: it logs lossily and prunes after 30 days, so a
 * missing line may never have been written.
 */
function NodeLogs() {
  const { nodeId } = Route.useParams();
  const [level, setLevel] = useState<LevelFilter>("notice");
  const [text, setText] = useState("");
  const [limit, setLimit] = useState(PAGE);

  // `source:` narrows by source prefix; anything else searches the message.
  const query = useDeferredValue(text.trim());
  const sourceMatch = /^source:(\S+)\s*(.*)$/.exec(query);
  const source = sourceMatch ? sourceMatch[1] : null;
  const search = (sourceMatch ? sourceMatch[2] : query) || null;

  const { data: rows, isLoading } = trpc.brewcontrol.listLogs.useQuery(
    { nodeId, level: level === "all" ? null : level, source, search, limit },
    { refetchInterval: POLL_MS, placeholderData: (previous) => previous },
  );

  return (
    <div className="space-y-3 p-4">
      <div className="flex items-center gap-3">
        <ToggleGroup
          type="single"
          size="sm"
          variant="outline"
          value={level}
          onValueChange={(v) => v && setLevel(v as LevelFilter)}
        >
          <ToggleGroupItem value="notice">Notices</ToggleGroupItem>
          <ToggleGroupItem value="all">With debug</ToggleGroupItem>
        </ToggleGroup>
        <Input
          className="max-w-md"
          placeholder="Search messages, or source:link …"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setLimit(PAGE);
          }}
        />
      </div>

      {isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : !rows || rows.length === 0 ? (
        <p className="text-sm text-olive-11">
          {query
            ? "No log lines match."
            : "This node has not pushed any log lines yet. Lines arrive once it is connected and running a version that pushes its log."}
        </p>
      ) : (
        <>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-olive-6 text-left text-xs text-olive-11">
                <th className="w-44 py-1 pr-3 font-normal">Time</th>
                <th className="w-16 py-1 pr-3 font-normal">Level</th>
                <th className="w-56 py-1 pr-3 font-normal">Source</th>
                <th className="py-1 font-normal">Message</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-olive-4">
              {rows.map((row) => (
                <tr
                  key={row.nodeLogId}
                  className={row.level === "debug" ? "align-top text-olive-11" : "align-top"}
                >
                  <td className="py-1 pr-3 font-mono text-xs whitespace-nowrap">
                    {format(new Date(row.loggedAt), "yyyy-MM-dd HH:mm:ss.SSS")}
                  </td>
                  <td className="py-1 pr-3 text-xs">{row.level}</td>
                  <td className="py-1 pr-3 font-mono text-xs break-all">{row.source}</td>
                  <td className="py-1 break-words">
                    {row.message}
                    {row.payload !== null && (
                      <span className="ml-2 font-mono text-xs text-olive-11 break-all">
                        {JSON.stringify(row.payload)}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === limit && limit < MAX_ROWS && (
            <Button variant="ghost" onClick={() => setLimit((l) => Math.min(l + PAGE, MAX_ROWS))}>
              Load older
            </Button>
          )}
        </>
      )}
    </div>
  );
}
